import { TFunction } from "i18next";
import createLogger from "../../utils/logger.util";
import RuntimeError from "../../utils/runtimeError.util";
import Base from "../Base";

import { ReceiptStatus } from "../ReceiptProvider/types";
import { resolveReceiptArchiveReasonMessage } from "../../utils/receiptLastError.util";
import { resolveReceiptSkipReasonMessage } from "../../utils/receiptSkipReason.util";

const logger = createLogger({ name: "ReceiptTransaction" });

const mapOrder = (order: any): ReceiptTransaction.Order | null => {
	if (!order) return null;

	return {
		id: order.id,
		orderNumber: order.orderNumber,
		taxiService: order.taxiService
			? {
					id: order.taxiService.id,
					name: order.taxiService.name,
					settlement: order.taxiService.settlement ?? null,
					company: order.taxiService.company
						? {
								id: order.taxiService.company.id,
								name: order.taxiService.company.name,
						  }
						: null,
			  }
			: null,
		passengers: Array.isArray(order.passengers)
			? order.passengers.map((passenger: any) => ({
					id: passenger.id,
					rideNumber: passenger.rideNumber,
			  }))
			: [],
	};
};

const mapExecutor = (executor: any): ReceiptTransaction.Executor | null => {
	if (!executor) return null;

	return {
		id: executor.id,
		callSign: executor.callSign ?? null,
		person: executor.person
			? {
					name: executor.person.name,
					surname: executor.person.surname,
					fatherName: executor.person.fatherName,
					fullName: executor.person.fullName,
			  }
			: null,
	};
};

const mapCar = (
	car: any,
	requestPayload?: Record<string, any> | null,
): ReceiptTransaction.Car | null => {
	if (!car && !requestPayload?.car) return null;

	const payloadModel = String(requestPayload?.car?.model ?? "").trim();

	return {
		id: car?.id ?? 0,
		parkNumber: car?.callSign ?? null,
		registrationNumber:
			car?.additionalFields?.registrationNumber ??
			car?.registrationNumber ??
			null,
		brand:
			car?.modelToBodyType?.model?.baseTypeToBrand?.brand?.name ??
			car?.brand ??
			null,
		modelName:
			car?.modelToBodyType?.model?.name ?? car?.modelName ?? null,
		rideModel: payloadModel || null,
	};
};

class ReceiptTransaction extends Base {
	public static fromResponse(data: any): ReceiptTransaction.Model {
		return {
			id: data.id,
			status: data.status,
			skipReason: data.skipReason ?? null,
			externalId: data.externalId ?? null,
			downloadUrl: data.downloadUrl ?? null,
			mevLink: data.mevLink ?? null,
			attemptCount: data.attemptCount ?? 0,
			lastError: data.lastError ?? null,
			sentAt: data.sentAt ?? null,
			completedAt: data.completedAt ?? null,
			createdAt: data.createdAt,
			updatedAt: data.updatedAt,

			orderId: data.orderId,
			order: mapOrder(data.order),
			receiptProvider: data.receiptProvider ?? null,
			executor: mapExecutor(data.executor),
			car: mapCar(data.car, data.requestPayload),
			rideCourse: data.requestPayload?.ride?.course ?? null,
		};
	}

	public static formatCarModel(
		item?: Pick<ReceiptTransaction.Model, "car"> | null,
	): string {
		const car = item?.car;
		if (!car) return "";

		const fromParts = [car.brand, car.modelName]
			.filter(Boolean)
			.join(" ")
			.trim();

		if (fromParts) return fromParts;

		return car.rideModel?.trim() ?? "";
	}

	public static formatExecutorName(
		executor?: ReceiptTransaction.Executor | null,
	): string {
		const person = executor?.person;
		if (!person) return "";

		if (person.fullName) return person.fullName;

		return [person.surname, person.name, person.fatherName]
			.filter(Boolean)
			.join(" ");
	}

	public static resolveRideNumber(
		item: ReceiptTransaction.Model,
	): number | string | null {
		const rideNumber = item.order?.passengers?.[0]?.rideNumber;
		if (rideNumber != null) return rideNumber;

		return item.rideCourse ?? null;
	}

	public static resolveReasonMessage(
		t: TFunction,
		item?: ReceiptTransaction.Model | null,
	): string {
		if (!item) return "";

		if (item.status === ReceiptStatus.FAILED) {
			return resolveReceiptArchiveReasonMessage(t, item.lastError) ?? "";
		}

		if (item.status === ReceiptStatus.SKIPPED) {
			return resolveReceiptSkipReasonMessage(t, item.skipReason) ?? "";
		}

		return "";
	}

	public static canDownloadReceipt(
		item?: ReceiptTransaction.Model | null,
	): boolean {
		if (!item) return false;

		return (
			item.status === ReceiptStatus.SUCCESS &&
			Boolean(item.externalId || item.downloadUrl)
		);
	}

	public static async getAll(
		params: ReceiptTransaction.QueryParams = {},
	): Promise<ReceiptTransaction.Model[]> {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.receipt.transaction.getAll(params),
			);
			return (res?.items ?? []).map(this.fromResponse);
		} catch (error) {
			logger.error("[ReceiptTransaction] getAll error", error);
			return [];
		}
	}

	public static async getById(
		id: number,
	): Promise<ReceiptTransaction.Model | null> {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.receipt.transaction.getById(id),
			);
			return res ? this.fromResponse(res) : null;
		} catch (error) {
			logger.error("[ReceiptTransaction] getById error", error);
			return null;
		}
	}

	/**
	 * Ручной retry — повторная отправка чека в ECC.
	 * Используется в карточке заказа, если автоматический cron не успел восстановиться.
	 */
	public static async retry(
		id: number,
	): Promise<ReceiptTransaction.Model | null> {
		try {
			const res = await this.request(
				(prpc) => prpc.theirsModel.receipt.transaction.retry(id),
				{ silent: false, error: true },
			);
			return res ? this.fromResponse(res) : null;
		} catch (error) {
			logger.error("[ReceiptTransaction] retry error", error);
			throw error;
		}
	}

	public static async resolveRetryTransactionId(
		orderId: number,
		preferredId?: number | null,
	): Promise<number | null> {
		if (preferredId) return preferredId;

		const items = await this.getAll({
			orderIds: [orderId],
			limit: 5,
		});

		const failed = items.find(
			(item) => item.status === ReceiptStatus.FAILED,
		);

		return failed?.id ?? items[0]?.id ?? null;
	}

	/**
	 * Получает PNG/PDF чека через бэкенд-прокси из ECC по `transactionId` (файл у нас не хранится).
	 */
	public static async downloadImage(
		transactionId: number,
	): Promise<{
		blob: Blob;
		contentType: string;
		fileName: string;
		sizeBytes: number;
	} | null> {
		try {
			const res = await this.request((prpc) =>
				prpc.theirsModel.receipt.transaction.downloadImage(
					transactionId,
				),
			);
			if (!res?.data) return null;

			const binary = atob(res.data);
			const bytes = new Uint8Array(binary.length);
			for (let i = 0; i < binary.length; i++) {
				bytes[i] = binary.charCodeAt(i);
			}

			return {
				blob: new Blob([bytes], {
					type: res.contentType || "image/png",
				}),
				contentType: res.contentType || "image/png",
				fileName: res.fileName || `receipt-${transactionId}.png`,
				sizeBytes: res.sizeBytes ?? bytes.length,
			};
		} catch (error) {
			logger.error("[ReceiptTransaction] downloadImage error", error);
			return null;
		}
	}

	/**
	 * On-demand получение публичной ссылки налоговой (`mevLink`) по чеку.
	 * Нужно для ручной отправки SMS: по чекам без авто-SMS `mevLink` пустой,
	 * и при открытии модалки мы дотягиваем ссылку, чтобы подставить в текст.
	 */
	public static async ensureMevLink(
		transactionId: number,
	): Promise<string | null> {
		try {
			const res = await this.request((prpc) => {
				const ensureMevLink =
					prpc?.theirsModel?.receipt?.transaction?.ensureMevLink;
				if (typeof ensureMevLink !== "function") {
					throw new RuntimeError(
						"Метод receipt.transaction.ensureMevLink недоступен. Перезапустите gateway и обновите страницу.",
						501,
						"Not Implemented",
					);
				}
				return ensureMevLink(transactionId);
			});
			return res?.mevLink ?? null;
		} catch (error) {
			logger.error("[ReceiptTransaction] ensureMevLink error", error);
			return null;
		}
	}

	public static async sendSms(orderId: number): Promise<boolean> {
		try {
			const res = await this.request(
				(prpc) => {
					const sendSms = prpc?.theirsModel?.receipt?.transaction?.sendSms;
					if (typeof sendSms !== "function") {
						throw new RuntimeError(
							"Метод receipt.transaction.sendSms недоступен. Перезапустите gateway и обновите страницу.",
							501,
							"Not Implemented",
						);
					}
					return sendSms(orderId);
				},
				{ silent: false, error: true },
			);
			return Boolean(res?.sent);
		} catch (error) {
			logger.error("[ReceiptTransaction] sendSms error", error);
			throw error;
		}
	}
}

declare namespace ReceiptTransaction {
	interface LocalizedName {
		[key: string]: string;
	}

	interface Order {
		id: number;
		orderNumber?: number;
		taxiService: {
			id: number;
			name?: LocalizedName | null;
			settlement?: LocalizedName | null;
			company: {
				id: number;
				name?: LocalizedName | null;
			} | null;
		} | null;
		passengers: Array<{
			id?: number;
			rideNumber?: number;
		}>;
	}

	interface Executor {
		id: number;
		callSign?: string | null;
		person?: {
			name?: string;
			surname?: string;
			fatherName?: string;
			fullName?: string;
		} | null;
	}

	interface Car {
		id: number;
		parkNumber?: string | null;
		registrationNumber?: string | null;
		brand?: string | null;
		modelName?: string | null;
		rideModel?: string | null;
	}

	interface Model {
		id: number;
		status: ReceiptStatus;
		skipReason: string | null;
		externalId: string | null;
		downloadUrl: string | null;
		mevLink: string | null;
		attemptCount: number;
		lastError: string | null;
		sentAt: string | null;
		completedAt: string | null;
		createdAt: string;
		updatedAt: string;
		orderId: number;
		order: Order | null;
		receiptProvider: { id: number; name: string } | null;
		executor: Executor | null;
		car: Car | null;
		rideCourse?: string | null;
	}

	interface QueryParams {
		limit?: number;
		offset?: number;
		taxiServiceIds?: number[];
		companyIds?: number[];
		providerIds?: number[];
		orderIds?: number[];
		executorIds?: number[];
		carIds?: number[];
		statuses?: ReceiptStatus[];
		from?: Date;
		to?: Date;
	}
}

export default ReceiptTransaction;
